<template>
  <layout-report :renderBackAndForthFooter="false" :isProgressDone="true" :showFooter="false">
    <v-container class="padded" v-if="!isLoading">
      <v-row>
        <v-col cols="12">
          <v-card flat>
            <v-card-title class="text-h4 px-0">
              {{ title }}
            </v-card-title>
            <v-card-subtitle v-html="description" class="pl-0"></v-card-subtitle>

            <v-card-actions v-if="!isThgs">
              <v-btn @click="newCar" color="success" large outlined data-test-submit-register>
                {{ $t("report.thgVehicleOverview.btnTxt") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <notifications />
      <v-row v-if="!newVehicleView">
        <v-col cols="12" class="px-1">
          <v-row align="center" no-gutters>
            <v-btn-toggle class="mx-2" v-model="activeView">
              <v-btn small icon @click="changeToCardView"> <v-icon>mdi-apps</v-icon></v-btn>
              <v-btn small icon @click="changeToListView"> <v-icon>mdi-view-list</v-icon></v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-btn small text @click="isDialogActive = true" class="mr-2" outlined>
              {{ $t("views.fleet.FleetHomeVehicleTable.importVehicle") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="!isLoadingAllThgs">
        <v-col cols="12" class="px-1" v-if="newVehicleView">
          <thg-my-vehicles :myThgs="myThgs"></thg-my-vehicles>
        </v-col>
        <v-col v-else cols="12" class="px-1">
          <thg-vehicle-table v-if="showVehicleTable" :myThgs="myThgs" @refresh="loadAll" />
          <thg-vehicle-card-iterator v-else :myThgs="myThgs" @refresh="loadAll" />
        </v-col>

        <thg-import-dialog :isDialogActive.sync="isDialogActive" @imported="loadAll"> </thg-import-dialog>
      </v-row>
      <v-row v-else>
        <v-col offset="0" cols="12">
          <v-card class="mb-10" max-width="515">
            <v-skeleton-loader class="mx-auto" type="card-avatar, article, article, actions"></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
      <v-btn elevation="6" :color="color" @click="newCar" fab fixed bottom right>
        <v-icon color="white"> mdi-plus </v-icon>
      </v-btn>
      <v-subheader class="mb-6 mt-6"><small v-html="note" /></v-subheader>
    </v-container>
    <v-container class="padded" v-else-if="isLoading">
      <v-row>
        <v-card flat max-width="500">
          <v-card-text>
            <p class="display-1 text--primary">{{ title }}</p>
          </v-card-text>
        </v-card>
      </v-row>
      <v-row>
        <v-col offset="0" cols="12">
          <v-card class="mb-10" max-width="515">
            <v-skeleton-loader class="mx-auto" type="card-avatar, article, article, actions"></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </layout-report>
</template>

<script lang="ts">
import Notifications from "@/components/thg/Notifications.vue";
import ThgVehicleCard from "@/components/thg/ThgVehicleCard.vue";
import ThgVehicleCardIterator from "@/components/thg/ThgVehicleCardIterator.vue";
import ThgVehicleTable from "@/components/thg/ThgVehicleTable.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { PartnerTypeEnum } from "@/store/enum/partner/partner.type.enum";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { ThgModule } from "@/store/modules/thg.store";
import { UserModule } from "@/store/modules/me-user.store";
import { Component, Vue } from "vue-property-decorator";
import { ThgThgMeViewModelGen } from "@/services/thg/v1/data-contracts";
import ThgMyVehicles from "@/components/thg/ThgMyVehicles.vue";
import { FeatureModule } from "@/store/modules/feature.store";
import ThgImportDialog from "@/components/thg/ThgImportDialog.vue";

@Component({
  components: {
    ThgVehicleCard,
    LayoutReport,
    ThgVehicleTable,
    ThgMyVehicles,
    ThgVehicleCardIterator,
    Notifications,
    ThgImportDialog
  }
})
export default class ThgVehicleOverview extends Vue {
  verificationCode = "";
  isLoading = true;
  valid = false;
  showVehicleTable = false;
  activeView = 0;

  isDialogActive = false;

  get newVehicleView() {
    return FeatureModule.isNewVehicleView;
  }

  get isLoadingAllThgs() {
    return ThgModule.isLoading;
  }

  get isThgs() {
    if (this.myThgs.length === 0) {
      return false;
    } else {
      return true;
    }
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get myThgs(): ThgThgMeViewModelGen[] {
    return ThgModule.myThgs;
  }

  get isAuthenticated(): boolean {
    return UserModule.isAuthenticated;
  }

  get description() {
    if (this.isThgs) {
      return this.$t("report.thgVehicleOverview.description").toString();
    } else {
      return this.$t("report.thgVehicleOverview.descriptionAlt").toString();
    }
  }

  get note() {
    if (this.isWhiteLabelPartner) {
      return this.$t("report.thgImpactFactor.noteWhiteLabelPartner").toString();
    } else {
      return this.$t("report.thgImpactFactor.note").toString();
    }
  }

  get partner() {
    return ReportModule.partner;
  }

  get title() {
    return this.$t("report.thgVehicleOverview.title").toString();
  }

  get isWhiteLabelPartner(): boolean {
    return PartnerModule?.partner.partnerType === PartnerTypeEnum.THG_WHITE_LABEL_QUOTE_AS_A_SERVICE;
  }

  async mounted() {
    await this.loadAll();
  }

  async loadAll() {
    try {
      this.isLoading = true;
      await ThgModule.getMyThgs();
      if (ThgModule.myThgs.length > 10) {
        this.showVehicleTable = true;
        this.activeView = 1;
      }
    } catch {
      this.$toast.error(this.$t("report.thgVehicleOverview.errorTxt").toString());
    } finally {
      this.isLoading = false;
    }
  }

  newCar() {
    this.$router.push({ path: "/thghome" });
  }

  changeToListView() {
    this.showVehicleTable = true;
  }

  changeToCardView() {
    this.showVehicleTable = false;
  }
}
</script>

<style scoped>
.contain {
  width: 100%;
  padding: 1em;
}
</style>
